import { Alert, flash, FormGroup, Label, Modal, SubmitButton } from "@xola/ui-kit";
import { SecondaryButton } from "common";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { Except } from "type-fest";
import { api } from "../../../api/api";
import { useSeller } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { CurrencyInput } from "../../Input/CurrencyInput";

interface RefundReserveModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const RefundReserveModal = ({ isOpen, onClose }: RefundReserveModalProps) => {
    return (
        <Modal shouldCloseOnOutsideClick size="huge" isOpen={isOpen} onClose={onClose}>
            <ModalContent onClose={onClose} />
        </Modal>
    );
};

const ModalContent = ({ onClose }: Except<RefundReserveModalProps, "isOpen">) => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const { escrow } = seller.preferences.payout;

    const [desiredAmount, setDesiredAmount] = useState(escrow?.desiredAmount ?? 0);
    const { mutateAsync: handleSaveRefundReserve, isLoading } = useMutation(async () => {
        await api.post(`/sellers/${seller.id}/preferences/payout`, { escrow: { desiredAmount } });
        invalidate("sellers", seller.id);
        onClose();
        flash.success("Refund Reserve updated");
    });

    return (
        <>
            <Modal.Header>Edit Refund Reserve</Modal.Header>
            <Modal.Body>
                <div>
                    <FormGroup>
                        <Label>Desired Balance</Label>
                        <CurrencyInput
                            value={desiredAmount}
                            onValueChange={({ value }) => setDesiredAmount(Number(value))}
                        />
                    </FormGroup>
                </div>
                <Alert className="rounded p-4 leading-5">
                    <div className="text-base">
                        The Refund Reserve allows you to set aside a specified amount of money to cover any refunds you
                        might need to process.
                    </div>

                    <div className="mt-3 text-base">
                        Before configuring your &quot;Minimum Refund Reserve&quot; please read our{" "}
                        <a
                            href="https://help.xola.com/hc/en-us/articles/360044669831"
                            target="_blank"
                            className="font-bold text-blue"
                            rel="noreferrer"
                        >
                            help center article.
                        </a>{" "}
                        The article will help you understand the important changes to Payout due to this feature
                    </div>
                </Alert>
            </Modal.Body>
            <Modal.Footer className="space-x-4">
                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                <SubmitButton isLoading={isLoading} onClick={handleSaveRefundReserve}>
                    Save
                </SubmitButton>
            </Modal.Footer>
        </>
    );
};
